import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Sparkles, BookMarked, AlertTriangle, Clock, ExternalLink, X, Plus, RefreshCw, Check, Lightbulb, Play, ArrowRight, Loader2, Crosshair } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import api, { formatApiErrorDetail } from "../lib/api";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const slug = (s) => s.replace(/\s+/g, "-").toLowerCase();

const SectionLabel = ({ children, className = "" }) => (
  <p className={`text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase ${className}`}>{children}</p>
);

const ActionBtn = ({ onClick, disabled, testid, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAE5DB] bg-white px-3 py-1.5 text-[12px] font-bold text-[#54505B] hover:bg-[#181621] hover:text-white hover:border-[#181621] transition-colors disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-[#54505B] disabled:hover:border-[#EAE5DB]"
    data-testid={testid}
  >
    {children}
  </button>
);

const CompBadge = ({ name, picture, testid }) => (
  <span className="inline-flex items-center gap-1.5 min-w-0" data-testid={testid}>
    {picture ? (
      <img src={picture} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" referrerPolicy="no-referrer" />
    ) : (
      <span className="w-5 h-5 rounded-full bg-[#181621] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{(name || "?")[0].toUpperCase()}</span>
    )}
    <span className="font-bold text-[13px] text-[#181621] truncate">{name}</span>
  </span>
);

const AnalysisBlock = ({ a, comp, testid }) => (
  <div className="rounded-xl bg-[#FBFAF7] border border-[#F3EFE7] p-4 mt-3 space-y-2.5" data-testid={testid}>
    {comp && (
      <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#B4552D] uppercase flex items-center gap-1.5" data-testid={`${testid}-competitor`}>
        <Sparkles size={10} strokeWidth={2} /> AI analysis — {comp}
      </p>
    )}
    {[["Angle", a.angle], ["Hook", a.hook], ["Offer", a.offer], ["Format", a.format_notes], ["Why it works", a.why_it_works]].map(
      ([label, val]) => val && (
        <div key={label}>
          <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">{label}</p>
          <p className="text-[12.5px] text-[#181621] leading-snug mt-0.5">{val}</p>
        </div>
      )
    )}
    {a.counter_move && (
      <div className="rounded-lg bg-white border border-[#F2D8CE] border-l-4 border-l-[#E0603A] p-3">
        <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#B4552D] uppercase flex items-center gap-1.5"><Crosshair size={10} strokeWidth={2} /> Your counter-move</p>
        <p className="text-[12.5px] text-[#181621] leading-snug mt-1 font-medium">{a.counter_move}</p>
      </div>
    )}
  </div>
);

const SetAnalysisCard = ({ a, comp }) => (
  <div className="card-soft p-6 sm:p-7 mt-5" data-testid="spy-set-analysis">
    <SectionLabel>Set analysis — what {comp || "this competitor"} is really doing</SectionLabel>
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-4">
      {[["Angles", a.angles], ["Hooks", a.hooks], ["Offers", a.offers]].map(([label, arr]) => (
        <div key={label} className={label === "Angles" ? "sm:col-span-2" : ""}>
          <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">{label}</p>
          <ul className="mt-1.5 space-y-1">
            {(arr || []).map((x, i) => (
              <li key={i} className="text-[13px] text-[#181621] leading-snug flex gap-2">
                <span className="text-[#E0603A] shrink-0">—</span>{x}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {a.formats && (
        <div>
          <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">Format mix</p>
          <p className="text-[13px] text-[#181621] leading-snug mt-1.5">{a.formats}</p>
        </div>
      )}
      {a.patterns && (
        <div>
          <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">Frequency patterns</p>
          <p className="text-[13px] text-[#181621] leading-snug mt-1.5">{a.patterns}</p>
        </div>
      )}
    </div>
    {a.counter_move && (
      <div className="rounded-xl bg-white border border-[#F2D8CE] border-l-4 border-l-[#E0603A] p-4 mt-5">
        <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#B4552D] uppercase flex items-center gap-1.5"><Crosshair size={10} strokeWidth={2} /> Your strongest counter-move</p>
        <p className="text-[13.5px] text-[#181621] leading-relaxed mt-1.5 font-medium" data-testid="spy-set-counter-move">{a.counter_move}</p>
      </div>
    )}
  </div>
);

const AdMedia = ({ ad, onFail }) => {
  const [imgOk, setImgOk] = useState(true);
  const img = (ad.image_urls || [])[0];
  const vid = (ad.videos || [])[0] || (ad.video_urls?.[0] ? { url: ad.video_urls[0] } : null);
  if (img && imgOk) {
    return <img src={img} alt={ad.page_name} loading="lazy" className="w-full aspect-[4/3] object-contain bg-[#F3EFE7]" onError={() => { setImgOk(false); if (!vid?.url && !vid?.thumb) onFail?.(); }} referrerPolicy="no-referrer" />;
  }
  if (vid?.url) {
    return <video src={vid.url} poster={vid.thumb || undefined} controls preload="none" className="w-full aspect-[4/3] object-contain bg-[#181621]" data-testid="spy-ad-video" />;
  }
  if (vid?.thumb) {
    return (
      <a href={ad.library_url || "#"} target="_blank" rel="noreferrer" className="relative block">
        <img src={vid.thumb} alt={ad.page_name} loading="lazy" className="w-full aspect-[4/3] object-contain bg-[#F3EFE7]" referrerPolicy="no-referrer" onError={() => { setImgOk(false); onFail?.(); }} />
        <span className="absolute inset-0 flex items-center justify-center"><Play size={28} className="text-white drop-shadow" /></span>
      </a>
    );
  }
  return null;
};

const AdCard = ({ ad, idx, compName, compPicture, onAnalyze, onInspire, onReference, analyzing, inspiring }) => {
  const [mediaFailed, setMediaFailed] = useState(false);
  const libraryUrl = ad.library_url || (ad.ad_archive_id ? `https://www.facebook.com/ads/library/?id=${ad.ad_archive_id}` : null);
  const hasMedia = !mediaFailed && ((ad.image_urls || []).length || (ad.videos || []).length || (ad.video_urls || []).length);
  return (
  <div className="card-soft overflow-hidden flex flex-col" data-testid={`spy-ad-card-${idx}`}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#F3EFE7]">
      <CompBadge name={compName || ad.page_name} picture={compPicture} testid={`spy-ad-page-${idx}`} />
      {libraryUrl && (
        <a href={libraryUrl} target="_blank" rel="noreferrer" className="ml-auto shrink-0 text-[11px] font-semibold text-[#54505B] hover:text-[#181621] flex items-center gap-1" data-testid={`spy-ad-library-${idx}`}>
          Ads Library <ExternalLink size={11} strokeWidth={1.8} />
        </a>
      )}
    </div>
    {!mediaFailed && <AdMedia ad={ad} onFail={() => setMediaFailed(true)} />}
    <div className="p-4 flex flex-col flex-1">
      <div className="flex flex-wrap gap-1.5">
        {!hasMedia && <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-[#FBFAF7] px-2.5 py-0.5 text-[10.5px] font-bold text-[#54505B] uppercase tracking-[0.05em]">Text ad</span>}
        {ad.display_format && <span className="inline-flex items-center rounded-full border border-[#BCD4F2] bg-white px-2.5 py-0.5 text-[10.5px] font-bold text-[#3565A8] uppercase tracking-[0.05em]">{ad.display_format}</span>}
        {(ad.platforms || []).slice(0, 3).map((p) => (
          <span key={p} className="inline-flex items-center rounded-full border border-[#BCD4F2] bg-white px-2 py-0.5 text-[10.5px] font-bold text-[#3565A8] uppercase tracking-[0.05em]">{p.slice(0, 2)}</span>
        ))}
        {ad.run_duration_days > 30 && (
          <span className="inline-flex items-center rounded-full border border-[#BFDDB8] bg-white px-2.5 py-0.5 text-[10.5px] font-bold text-[#3E7B3E] uppercase tracking-[0.05em] tabular-nums">Running {ad.run_duration_days}d</span>
        )}
      </div>
      <p className="text-[11px] text-[#9A9591] mt-2 flex items-center gap-1 tabular-nums"><Clock size={11} strokeWidth={1.8} /> Since {fmtDate(ad.start_date)}</p>
      {ad.headline && <p className="text-[13px] font-semibold text-[#181621] mt-2 leading-snug">{ad.headline}</p>}
      <p className="text-[12.5px] text-[#54505B] mt-1.5 leading-snug line-clamp-4 whitespace-pre-line flex-1" data-testid={`spy-ad-copy-${idx}`}>
        {ad.ad_copy || "No primary text."}
      </p>
      {ad.analysis && <AnalysisBlock a={ad.analysis} comp={compName || ad.page_name} testid={`spy-ad-analysis-${idx}`} />}
      <div className="flex flex-wrap gap-2 mt-3.5 pt-3 border-t border-[#F3EFE7]">
        {!ad.analysis && (
          <ActionBtn onClick={onAnalyze} disabled={analyzing} testid={`spy-ad-analyze-${idx}`}>
            <Sparkles size={12} strokeWidth={1.8} /> {analyzing ? "Analyzing…" : "Analyze"}
          </ActionBtn>
        )}
        <ActionBtn onClick={onInspire} disabled={inspiring} testid={`spy-ad-inspire-${idx}`}>
          <Lightbulb size={12} strokeWidth={1.8} /> {inspiring ? "Extracting angle…" : "Make it yours"}
        </ActionBtn>
        <ActionBtn onClick={onReference} testid={`spy-ad-ref-${idx}`}>
          <BookMarked size={12} strokeWidth={1.8} /> Reference Lab
        </ActionBtn>
      </div>
    </div>
  </div>
  );
};

const BuildEventLine = ({ ev }) => {
  const icon = ev.status === "ok" ? <Check size={13} strokeWidth={2.5} className="text-[#3E7B3E]" />
    : ev.status === "dropped" ? <X size={13} strokeWidth={2.5} className="text-[#B4552D]" />
    : ev.status === "queued" ? <span className="w-[13px] h-[13px] rounded-full border border-[#E7E2D6] inline-block" />
    : <Loader2 size={13} strokeWidth={2} className="animate-spin text-[#0B67FF]" />;
  const label = ev.status === "resolving" ? `Finding ${ev.name}…`
    : ev.status === "fetching" ? `Pulling ${ev.name}'s active ads…`
    : ev.status === "ok" ? `${ev.name} — ${ev.ads_count} ads`
    : ev.status === "dropped" ? `${ev.name}: ${ev.note || "dropped"}`
    : ev.name;
  return (
    <div className="flex items-center gap-2.5 py-1" data-testid={`spy-build-event-${slug(ev.name)}`}>
      <span className="shrink-0 flex items-center">{icon}</span>
      <span className={`text-[13px] tabular-nums ${ev.status === "dropped" ? "text-[#9A9591] line-through decoration-[#E0603A]/40" : "text-[#181621]"} ${ev.status === "ok" ? "font-semibold" : ""}`}>{label}</span>
      {ev.replaces && <span className="text-[10px] font-bold tracking-[0.06em] text-[#B4552D] uppercase">Replacing {ev.replaces}</span>}
    </div>
  );
};

export default function AdsSpy() {
  const [usage, setUsage] = useState(null);
  const [country, setCountry] = useState("IT");
  const [competitors, setCompetitors] = useState(undefined);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [addInput, setAddInput] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [filter, setFilter] = useState(null);
  const [building, setBuilding] = useState(false);
  const [buildEvents, setBuildEvents] = useState([]);
  const [buildStage, setBuildStage] = useState("");
  const [replacedNote, setReplacedNote] = useState(null);
  const [refreshModal, setRefreshModal] = useState(false);
  const [refreshingComp, setRefreshingComp] = useState(null);
  const [analyzingAd, setAnalyzingAd] = useState(null);
  const [analyzingSet, setAnalyzingSet] = useState(false);
  const [inspiringAd, setInspiringAd] = useState(null);
  const pollRef = useRef(null);
  const countryRef = useRef("IT");
  const autoRanRef = useRef(false);
  const navigate = useNavigate();

  const stopPolling = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };
  const loadUsage = () => api.get("/ads-spy/usage").then(({ data }) => setUsage(data)).catch(() => {});

  const loadDashboard = async (c) => {
    try {
      const { data } = await api.get("/ads-spy/dashboard", { params: { country: c || countryRef.current } });
      setDashboard(data);
      return data;
    } catch { return null; }
  };

  const loadCompetitors = async () => {
    const { data } = await api.get("/ads-spy/competitors");
    setCompetitors(data.competitors);
    return data.competitors;
  };

  const showReplacedNote = (job) => {
    if (job?.replaced?.length && localStorage.getItem(`spy-swap-dismissed-${job.updated_at}`) !== "1") {
      setReplacedNote({ list: job.replaced, key: job.updated_at });
    }
  };

  const suggest = async (baseDraft) => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/ads-spy/competitors/suggest");
      const existing = new Set((baseDraft || []).map((c) => c.name.toLowerCase()));
      const fresh = data.suggestions.filter((s) => !existing.has(s.name.toLowerCase()));
      setDraft([...(baseDraft || []), ...fresh]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => {
    (async () => {
      loadUsage();
      let c = "IT";
      try {
        const { data } = await api.get("/brand-brain");
        if (data?.country?.length === 2) c = data.country.toUpperCase();
      } catch { /* default */ }
      setCountry(c);
      countryRef.current = c;
      let comps = [];
      try { comps = await loadCompetitors(); } catch { setCompetitors([]); }
      const dash = await loadDashboard(c);
      let jobPending = false;
      try {
        const { data: job } = await api.get("/ads-spy/status");
        if (job.status === "pending") {
          jobPending = true;
          if (job.kind === "autobuild") { setBuilding(true); setBuildEvents(job.events || []); setBuildStage(job.stage || ""); }
          startPolling();
        } else if (job.status === "complete" && job.kind === "autobuild") {
          showReplacedNote(job);
        }
      } catch { /* ignore */ }
      if (!comps.length) {
        setEditMode(true);
        suggest([]);
        return;
      }
      const totalAds = (dash?.competitors || []).reduce((n, g) => n + g.ads.length, 0);
      if (!jobPending && totalAds === 0 && !autoRanRef.current) {
        autoRanRef.current = true;
        autobuild(false);
      }
    })();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/ads-spy/status");
        if (data.status === "failed") {
          stopPolling();
          setBuilding(false); setRefreshingComp(null);
          setErrMsg(data.error || "The spy mission failed. Try again.");
          loadUsage();
        } else if (data.status === "complete") {
          stopPolling();
          loadUsage();
          setBuilding(false);
          setRefreshingComp(null);
          await Promise.all([loadDashboard(), loadCompetitors()]);
          if (data.kind === "autobuild") showReplacedNote(data);
          toast("Fresh competitor ads are in.");
        } else if (data.kind === "autobuild") {
          setBuildEvents(data.events || []);
          setBuildStage(data.stage || "");
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const autobuild = async (force) => {
    setErrMsg("");
    setRefreshModal(false);
    try {
      const { data } = await api.post("/ads-spy/autobuild", { country: countryRef.current, force });
      if (data.status === "complete") {
        await loadDashboard();
        toast("Everything is fresh — this build was free.");
        return;
      }
      setBuilding(true);
      setBuildEvents([]);
      setBuildStage("Building your spy dashboard…");
      loadUsage();
      startPolling();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveCompetitors = async (list) => {
    const { data } = await api.put("/ads-spy/competitors", { competitors: list.map(({ reason, ad_count, page_url, ...c }) => c) });
    setCompetitors(data.competitors);
    return data.competitors;
  };

  const confirmDraft = async () => {
    try {
      await saveCompetitors(draft);
      setEditMode(false);
      toast("Competitor list saved.");
      autobuild(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addDraftName = () => {
    const v = addInput.trim();
    if (v.length < 2) return;
    if (!draft.some((c) => c.name.toLowerCase() === v.toLowerCase())) setDraft([...draft, { name: v }]);
    setAddInput("");
  };

  const refreshComp = async (g) => {
    if (refreshingComp || building) return;
    setRefreshingComp(g.name);
    try {
      const { data } = await api.post("/ads-spy/search", { name: g.name, page_id: g.page_id, country: countryRef.current });
      if (data.status === "complete") {
        await loadDashboard();
        setRefreshingComp(null);
        toast(data.from_cache ? `${g.name} is already fresh — served from today's cache, free.` : `${g.name} refreshed.`);
      } else {
        loadUsage();
        startPolling();
      }
    } catch (err) {
      setRefreshingComp(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const patchAd = (sid, adIndex, patch) => {
    setDashboard((d) => ({
      ...d,
      competitors: d.competitors.map((g) =>
        g.search_id === sid ? { ...g, ads: g.ads.map((a) => (a.index === adIndex ? { ...a, ...patch } : a)) } : g
      ),
    }));
  };

  const analyzeAd = async (sid, adIndex, key) => {
    setAnalyzingAd(key);
    try {
      const { data } = await api.post("/ads-spy/analyze", { search_id: sid, ad_index: adIndex });
      patchAd(sid, adIndex, { analysis: data.analysis });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAnalyzingAd(null);
    }
  };

  const analyzeSet = async (sid) => {
    setAnalyzingSet(true);
    try {
      const { data } = await api.post("/ads-spy/analyze", { search_id: sid });
      setDashboard((d) => ({
        ...d,
        competitors: d.competitors.map((g) => (g.search_id === sid ? { ...g, set_analysis: data.analysis } : g)),
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAnalyzingSet(false);
    }
  };

  const inspireAd = async (sid, adIndex, key) => {
    setInspiringAd(key);
    try {
      const { data } = await api.post("/ads-spy/inspire", { search_id: sid, ad_index: adIndex });
      navigate("/app/performance/creative", { state: { inspiration: data.inspiration } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInspiringAd(null);
    }
  };

  const sendToReference = async (sid, adIndex) => {
    try {
      await api.post("/ads-spy/reference", { search_id: sid, ad_index: adIndex });
      toast("Saved to Reference Lab.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const dismissReplacedNote = () => {
    if (replacedNote?.key) localStorage.setItem(`spy-swap-dismissed-${replacedNote.key}`, "1");
    setReplacedNote(null);
  };

  const lookupsLeft = usage ? usage.limit - usage.used : null;
  const groups = dashboard?.competitors || [];
  const byDateDesc = (a, b) => (b.start_date || "").localeCompare(a.start_date || "");
  const activeGroups = groups.filter((g) => g.ads.length && (!filter || g.name === filter));
  const quota = filter ? 50 : Math.max(3, Math.ceil(30 / Math.max(1, activeGroups.length)));
  const feed = activeGroups
    .flatMap((g) => [...g.ads].sort(byDateDesc).slice(0, quota).map((ad) => ({ ...ad, _sid: g.search_id, _comp: g.name })))
    .sort(byDateDesc)
    .slice(0, filter ? 50 : 30);
  const filteredGroup = filter ? groups.find((g) => g.name === filter) : null;
  const compPictures = Object.fromEntries((competitors || []).map((c) => [c.name, c.page_picture]));

  return (
    <div className="p-6 sm:p-10 max-w-5xl" data-testid="ads-spy-page">
      <MonoLabel>Performance Studio · Ads Spy</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        See what your competitors are running right now
      </h1>
      {usage && (
        <div className="mt-5 max-w-sm" data-testid="spy-usage-counter">
          <div className="h-1.5 rounded-full bg-[#F3EFE7] overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${usage.limit ? Math.min(100, (usage.used / usage.limit) * 100) : 0}%`,
                background: lookupsLeft <= 0 ? "var(--rose-fg)" : lookupsLeft <= 1 ? "var(--amber-fg)" : "linear-gradient(90deg, #E0603A, #0B67FF)",
              }}
            />
          </div>
          <p className={`text-[11.5px] font-semibold mt-1.5 tabular-nums ${lookupsLeft <= 0 ? "text-[#B4574A]" : lookupsLeft <= 1 ? "text-[#96691E]" : "text-[#54505B]"}`}>
            {lookupsLeft} of {usage.limit} fresh lookups left today
          </p>
          <p className="text-[11px] text-[#9A9591] mt-1">A full dashboard build counts as one lookup · cached ads are free.</p>
        </div>
      )}

      {/* Competitors */}
      <div className="card-soft mt-8 p-6 sm:p-7" data-testid="spy-competitors-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <SectionLabel>{editMode ? "Your competitors — confirm the list" : "Your competitors"}</SectionLabel>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">Country</label>
            <input className="field-input !py-1.5 w-[60px] uppercase text-center !text-[12.5px] tabular-nums" value={country} maxLength={2}
              onChange={(e) => { const v = e.target.value.toUpperCase(); setCountry(v); countryRef.current = v; if (v.length === 2) loadDashboard(v); }}
              data-testid="spy-country-input" />
          </div>
        </div>

        {competitors === undefined && <div className="h-12 mt-4 animate-pulse bg-[#F3EFE7] rounded-xl" />}

        {editMode ? (
          <div className="mt-4" data-testid="spy-edit-mode">
            <p className="text-[12.5px] text-[#54505B]">
              {suggesting ? "Drafting competitors from your Brand Brain…" : "Remove the wrong ones, add your own, then confirm — we handle the rest automatically."}
            </p>
            <div className="flex flex-wrap gap-2 mt-3 min-h-[36px]">
              {suggesting && !draft.length && [1, 2, 3, 4, 5].map((i) => <span key={i} className="h-8 w-28 rounded-full bg-[#F3EFE7] animate-pulse" />)}
              {draft.map((c) => (
                <span key={c.name} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#EAE5DB] pl-3 pr-2 py-1.5 text-[12.5px] font-semibold text-[#181621] hover:border-[#9A9591] transition-colors" title={c.reason || c.page_name || ""} data-testid={`spy-draft-chip-${slug(c.name)}`}>
                  {c.page_picture && <img src={c.page_picture} alt="" className="w-[18px] h-[18px] rounded-full object-cover" referrerPolicy="no-referrer" />}
                  {c.name}
                  <button onClick={() => setDraft(draft.filter((x) => x.name !== c.name))} className="text-[#9A9591] hover:text-[#181621]" data-testid={`spy-draft-remove-${slug(c.name)}`}>
                    <X size={13} strokeWidth={2} />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <div className="flex items-center gap-2">
                <input className="field-input !py-2 !text-[13px] w-[200px]" placeholder="Add your own…" value={addInput} onChange={(e) => setAddInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDraftName(); } }} data-testid="spy-add-competitor-input" />
                <SecondaryButton className="!py-2 !px-3 !text-[12.5px]" onClick={addDraftName} data-testid="spy-add-competitor-btn"><Plus size={13} strokeWidth={2} /></SecondaryButton>
              </div>
              <SecondaryButton className="!py-2 !px-3.5 !text-[12.5px] flex items-center gap-1.5" onClick={() => suggest(draft)} disabled={suggesting} data-testid="spy-refresh-suggestions-btn">
                <RefreshCw size={12} strokeWidth={1.8} className={suggesting ? "animate-spin" : ""} /> Refresh suggestions
              </SecondaryButton>
              <CtaButton className="!py-2 !px-4 text-[13px]" onClick={confirmDraft} disabled={!draft.length || suggesting} data-testid="spy-confirm-competitors-btn">
                <Check size={13} strokeWidth={2} /> Confirm list
              </CtaButton>
            </div>
          </div>
        ) : (
          <div className="mt-4" data-testid="spy-spy-mode">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter(null)}
                className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-[12.5px] font-bold transition-colors ${!filter ? "bg-[#181621] text-white border-[#181621]" : "bg-white text-[#181621] border-[#EAE5DB] hover:border-[#181621]"}`}
                data-testid="spy-filter-all"
              >
                All competitors
              </button>
              {(competitors || []).map((c) => {
                const g = groups.find((x) => x.name === c.name);
                return (
                  <button
                    key={c.name}
                    onClick={() => setFilter(filter === c.name ? null : c.name)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-[12.5px] font-bold transition-colors ${filter === c.name ? "bg-[#181621] text-white border-[#181621]" : "bg-white text-[#181621] border-[#EAE5DB] hover:border-[#181621]"}`}
                    title={`${g?.ads.length || 0} ads`}
                    data-testid={`spy-competitor-chip-${slug(c.name)}`}
                  >
                    {c.page_picture ? (
                      <img src={c.page_picture} alt="" className="w-5 h-5 rounded-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${filter === c.name ? "bg-white text-[#181621]" : "bg-[#181621] text-white"}`}>{(c.name || "?")[0].toUpperCase()}</span>
                    )}
                    {c.name}
                    {g?.ads.length > 0 && (
                      <span className={`text-[10.5px] font-bold tabular-nums ${filter === c.name ? "text-white/70" : "text-[#3E7B3E]"}`}>{g.ads.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <button onClick={() => { setDraft(competitors || []); setEditMode(true); setFilter(null); }} className="text-[12px] font-semibold text-[#54505B] hover:text-[#181621] underline decoration-[#E7E2D6] underline-offset-4" data-testid="spy-edit-list-btn">
                Edit list
              </button>
              <SecondaryButton className="!py-1.5 !px-3 !text-[12px] flex items-center gap-1.5" onClick={() => setRefreshModal(true)} disabled={building} data-testid="spy-refresh-all-btn">
                <RefreshCw size={12} strokeWidth={1.8} /> Refresh all
              </SecondaryButton>
            </div>
          </div>
        )}
      </div>

      {/* Refresh-all confirmation modal */}
      {refreshModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#181621]/40 p-4" data-testid="spy-refresh-modal">
          <div className="card-soft bg-white max-w-md w-full p-6 sm:p-7">
            <SectionLabel>Refresh all competitors</SectionLabel>
            <p className="text-[14px] text-[#181621] font-bold mt-3">This rebuilds the whole dashboard automatically.</p>
            <p className="text-[12.5px] text-[#54505B] mt-1.5 leading-relaxed">
              It runs several searches under the hood but counts as <strong>one</strong> of your daily lookups. Competitors without active ads get swapped for better ones. Continue?
            </p>
            <div className="flex items-center justify-end gap-2.5 mt-5">
              <SecondaryButton className="!py-2 !px-4 !text-[13px]" onClick={() => setRefreshModal(false)} data-testid="spy-refresh-cancel-btn">Cancel</SecondaryButton>
              <CtaButton className="!py-2 !px-4 text-[13px]" onClick={() => autobuild(true)} data-testid="spy-refresh-confirm-btn">
                <RefreshCw size={13} strokeWidth={2} /> Refresh all
              </CtaButton>
            </div>
          </div>
        </div>
      )}

      {/* Replaced-competitors note */}
      {replacedNote && !building && (
        <div className="card-soft mt-5 p-4 flex items-start gap-3 border-l-4 border-l-[#E0603A]" data-testid="spy-replaced-note">
          <span className="w-8 h-8 rounded-lg bg-[#FDF1EC] border border-[#F2D8CE] flex items-center justify-center shrink-0">
            <RefreshCw size={13} strokeWidth={2} className="text-[#E0603A]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-[#181621]">
              We swapped {replacedNote.list.length} competitor{replacedNote.list.length > 1 ? "s" : ""} that {replacedNote.list.length > 1 ? "aren't" : "isn't"} running ads right now.
            </p>
            <p className="text-[12px] text-[#54505B] mt-0.5 flex flex-wrap gap-x-3">
              {replacedNote.list.map((r, i) => (
                <span key={i} className="inline-flex items-center gap-1">{r.dropped} <ArrowRight size={10} /> <strong>{r.replacement}</strong></span>
              ))}
            </p>
          </div>
          <button onClick={dismissReplacedNote} className="text-[#9A9591] hover:text-[#181621] shrink-0" data-testid="spy-replaced-dismiss"><X size={15} /></button>
        </div>
      )}

      {/* Building state */}
      {building && (
        <div className="card-soft mt-6 p-7 sm:p-8" data-testid="spy-building-state">
          <div className="flex items-center gap-3">
            <div className="icon-tile animate-pulse" style={{ background: "var(--blue-bg)" }}>
              <Eye size={18} strokeWidth={1.8} style={{ color: "var(--blue-fg)" }} />
            </div>
            <div>
              <p className="font-bold text-[15px] text-[#181621]" data-testid="spy-building-stage">{buildStage || "Building your spy dashboard…"}</p>
              <p className="text-[12px] text-[#54505B] mt-0.5">Pulling your competitors' latest creatives — all automatic.</p>
            </div>
          </div>
          <div className="mt-5 border-t border-[#F3EFE7] pt-4" data-testid="spy-build-events">
            {buildEvents.map((ev, i) => <BuildEventLine key={`${ev.name}-${i}`} ev={ev} />)}
            {!buildEvents.length && <p className="text-[12.5px] text-[#9A9591]">Warming up…</p>}
          </div>
        </div>
      )}

      {errMsg && !building && (
        <div className="card-soft mt-6 p-6 flex items-center gap-3" data-testid="spy-error-state">
          <div className="icon-tile !w-9 !h-9 shrink-0" style={{ background: "var(--amber-bg)" }}>
            <AlertTriangle size={16} strokeWidth={1.8} style={{ color: "var(--amber-fg)" }} />
          </div>
          <p className="font-semibold text-[13.5px] text-[#181621] flex-1">{errMsg}</p>
          <SecondaryButton className="!py-1.5 !px-3 !text-[12px]" onClick={() => autobuild(false)} data-testid="spy-retry-btn">Try again</SecondaryButton>
          <button onClick={() => setErrMsg("")} className="text-[#9A9591] hover:text-[#181621]" data-testid="spy-error-dismiss"><X size={15} /></button>
        </div>
      )}

      {/* Feed */}
      {!editMode && !building && feed.length > 0 && (
        <div className="mt-8" data-testid="spy-results">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2.5 flex-wrap">
              <p className="font-bold text-[14px] text-[#181621] tabular-nums">
                {feed.length} ads <span className="text-[#9A9591] font-semibold">· {filter || "all competitors"} · {dashboard?.country || country}</span>
              </p>
              {!filter && <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-[#FBFAF7] px-2.5 py-0.5 text-[10.5px] font-bold text-[#54505B] uppercase tracking-[0.05em]">Newest first</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {filteredGroup?.page_id && (
                <SecondaryButton className="!py-2 !px-3.5 !text-[12.5px] flex items-center gap-1.5" onClick={() => refreshComp(filteredGroup)} disabled={refreshingComp !== null || building} data-testid="spy-refresh-one-btn">
                  <RefreshCw size={12} strokeWidth={1.8} className={refreshingComp === filteredGroup.name ? "animate-spin" : ""} />
                  {refreshingComp === filteredGroup.name ? "Refreshing…" : `Refresh ${filter}`}
                </SecondaryButton>
              )}
              {filteredGroup?.search_id && !filteredGroup.set_analysis && filteredGroup.ads.length > 0 && (
                <CtaButton className="!py-2 !px-4 text-[13px]" onClick={() => analyzeSet(filteredGroup.search_id)} disabled={analyzingSet} data-testid="spy-set-analyze-btn">
                  <Sparkles size={13} strokeWidth={1.8} /> {analyzingSet ? "Analyzing the set…" : "Analyze the whole set"}
                </CtaButton>
              )}
            </div>
          </div>

          {filteredGroup?.set_analysis && <SetAnalysisCard a={filteredGroup.set_analysis} comp={filteredGroup.name} />}

          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            {feed.map((ad) => {
              const key = `${ad._sid}-${ad.index}`;
              return (
                <AdCard
                  key={ad.ad_archive_id || key}
                  ad={ad}
                  idx={key}
                  compName={ad._comp}
                  compPicture={compPictures[ad._comp]}
                  onAnalyze={() => analyzeAd(ad._sid, ad.index, key)}
                  onInspire={() => inspireAd(ad._sid, ad.index, key)}
                  onReference={() => sendToReference(ad._sid, ad.index)}
                  analyzing={analyzingAd === key}
                  inspiring={inspiringAd === key}
                />
              );
            })}
          </div>
        </div>
      )}

      {!editMode && !building && feed.length === 0 && (
        <div className="card-soft mt-6 p-10 text-center" data-testid="spy-empty-state">
          <div className="icon-tile !w-12 !h-12 mx-auto" style={{ background: "var(--neutral-bg)" }}>
            <Eye size={20} strokeWidth={1.8} style={{ color: "var(--neutral-fg)" }} />
          </div>
          <p className="font-bold text-[15px] text-[#181621] mt-4">
            {filter ? `No ads loaded for ${filter} right now.` : "Your spy dashboard is empty."}
          </p>
          <p className="text-[12.5px] text-[#54505B] mt-1.5">
            {filter ? "They'll show up on the next refresh if they start running ads." : "One click builds it — pages, ads and swaps handled automatically."}
          </p>
          {!filter && (
            <CtaButton className="mt-5" onClick={() => autobuild(false)} data-testid="spy-build-btn">
              <Eye size={14} strokeWidth={2} /> Build my dashboard
            </CtaButton>
          )}
        </div>
      )}
    </div>
  );
}
